import { Button } from "$components/button/button";
import { StatusChip } from "$components/chip";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { BinIcon, CloseIcon } from "$components/icons";
import { SelectField, TextField } from "$components/rhf-fields";
import { SectionSubtitle, SectionTitle } from "$components/section";
import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  isServerTimeUnknownError,
  serverTimeUnknownMessage,
  useServerTime,
} from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { Invoice } from "$routes/_lib/billing-v2/invoice";
import type { InvoiceTransaction } from "$routes/_lib/billing-v2/invoice-source";
import { randomUniqueId } from "$routes/_lib/random-id";
import {
  useConvertFromPenceFactory,
  useConvertFromPoundsFactory,
} from "$routes/_lib/use-convert-currency";
import useCurrencyFormat from "$routes/_lib/use-currency-format";
import { invoiceRecordFromDocument } from "$routes/patient/[:patientId]/_lib/invoices-v2/invoice-record-from-document";
import { yupResolver } from "@hookform/resolvers/yup";
import { IconButton, styled, Typography } from "@material-ui/core";
import {
  PayerType,
  PaymentMethod,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import { useCallback, useMemo, useState } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import type { RxDocument } from "rxdb";
import { array, mixed, number, object } from "yup";
import { useFetchPatientNameRxdb } from "../use-fetch-patient-name-rxdb";
import { updateWithInsurerChargeIds } from "./update-with-insurer-charge-ids";

type FormMethod = typeof PaymentMethod.Cash | typeof PaymentMethod.Other;

// `""` is the handoff's empty amount: a split row added with nothing left to
// allocate renders blank rather than a literal 0.
type FormRow = { method: FormMethod; amount: number | "" };
type FormValues = { transactions: FormRow[] };

export type BalanceStatus = "valid" | "invalid" | "disabled";

const AMOUNT_FIELD_WIDTH = 168;

const HeaderBand = styled("div")(({ theme }) => ({
  padding: theme.spacing(3, 3, 2),
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

/**
 * `SectionTitle` / `SectionSubtitle` carry section-level spacing for use in a
 * page body; in the dialog header the band owns the spacing instead. Selecting
 * the rendered elements by tag outranks the components' own root class, so this
 * does not depend on stylesheet injection order.
 */
const HeaderTitles = styled("div")({
  "& > h2, & > h3": { padding: 0 },
  "& > h3": { marginTop: 2 },
});

const SummaryBand = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "flex-end",
  justifyContent: "space-between",
  gap: theme.spacing(3),
  padding: theme.spacing(2.5, 3),
  background: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const SummaryStats = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(3.5),
}));

const StatCaption = styled(Typography)(({ theme }) => ({
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: theme.palette.text.secondary,
}));

const OwedAmount = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  letterSpacing: "-0.01em",
}));

const QuietIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const CurrencyAdornment = styled("span")(({ theme }) => ({
  marginRight: theme.spacing(0.75),
  color: theme.palette.text.secondary,
}));

const MethodsBand = styled("div")(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 0.5),
}));

const MethodsLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
}));

const MethodRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
}));

const MethodCell = styled("div")({
  flex: "1 1 auto",
  minWidth: 0,
});

const AmountCell = styled("div")({
  flex: `0 0 ${AMOUNT_FIELD_WIDTH}px`,
});

const RemoveCell = styled("div")(({ theme }) => ({
  flex: "0 0 40px",
  paddingTop: theme.spacing(1),
}));

/**
 * The outlined field insets its text by 14px and the small text button pads
 * itself by 5px; the remaining 9px optically aligns the split link with the
 * field text above it.
 */
const SplitAction = styled("div")({
  marginLeft: 9,
});

const BalanceStrip = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  margin: theme.spacing(2.5, 3, 0),
  padding: theme.spacing(1.5, 2),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: 6,
}));

const BalanceReadout = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  "& span": {
    color: theme.palette.text.secondary,
  },
}));

// StatusChip title-cases its label, which mangles the sentence-shaped
// balance messages ("₦5.00 Still To Allocate").
const BalanceChip = styled(StatusChip)({
  textTransform: "none",
});

const FooterBand = styled("div")(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 3),
}));

const SecondaryActions = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginRight: "auto",
}));

/**
 * Single source of truth for the take-payment sum-vs-due checks. Derived from
 * the watched rows so the UI (balance strip, submit gate) never disagrees and
 * no imperative re-validation is needed.
 *  - `isOver`     — entered total exceeds the patient residual.
 *  - `isComplete` — the total is >0 and exactly settles the residual.
 *  - `canSubmit`  — every row is positive and the total is >0 and not over.
 *                   Partial payments are deliberately allowed; the per-row
 *                   positivity keeps the button in step with the yup schema,
 *                   which rejects a zero amount.
 */
export const derivePaymentSummary = (
  rows: readonly Partial<FormRow>[] | undefined,
  patientDueInPence: number,
  poundsToPence: (amountInPounds: number) => number,
): {
  sumInPence: number;
  diffInPence: number;
  allPositive: boolean;
  isOver: boolean;
  isComplete: boolean;
  canSubmit: boolean;
  balanceStatus: BalanceStatus;
} => {
  const list = rows ?? [];
  const sumInPence = list.reduce(
    (acc, r) => acc + (poundsToPence(Number(r?.amount) || 0) || 0),
    0,
  );
  const allPositive =
    list.length > 0 && list.every((r) => Number(r?.amount) > 0);
  const diffInPence = sumInPence - patientDueInPence;
  const isOver = diffInPence > 0;
  const isComplete = sumInPence > 0 && diffInPence === 0;

  let balanceStatus: BalanceStatus = "disabled";
  if (isComplete) balanceStatus = "valid";
  else if (isOver) balanceStatus = "invalid";

  return {
    sumInPence,
    diffInPence,
    allPositive,
    isOver,
    isComplete,
    canSubmit: allPositive && sumInPence > 0 && !isOver,
    balanceStatus,
  };
};

const SUM_PATH = "transactions";

const useMethodOptions = (): { value: FormMethod; label: string }[] => {
  const { formatMessage } = useIntl();
  return useMemo(
    () => [
      {
        value: PaymentMethod.Cash,
        label: formatMessage({
          id: "invoicing.takePaymentV2.method.cash",
          defaultMessage: "Cash",
        }),
      },
      {
        value: PaymentMethod.Other,
        label: formatMessage({
          id: "invoicing.takePaymentV2.method.other",
          defaultMessage: "Other",
        }),
      },
    ],
    [formatMessage],
  );
};

/**
 * V2 take-payment modal. Renders a per-row method selector (Cash /
 * Mobile money / Card) and a live balance strip reconciling the entered
 * total against the patient residual (`patientPayer.balance.due`) that was
 * already residualised by PNE-321 + PNE-327 (insurer claim line items).
 * On submit, runs `applyPayment` and persists via `doc.incrementalPatch(...)`.
 * No apollo `takePayment` mutation.
 *
 * Partial payments are allowed: the strip reports the shortfall but only an
 * over-allocation blocks submit.
 *
 * Waive / Exempt are not handled here — the action bar opens them as
 * separate modals (see `WaiveInvoiceModalV2`, `ExemptInvoiceModalV2`).
 */
export const TakePaymentModalV2 = ({
  doc,
  open,
  onClose,
  onWaiveClick,
  onExemptClick,
}: {
  doc: RxDocument<RxInvoice>;
  open: boolean;
  onClose: () => void;
  /** Optional. When provided, renders a "Waive" secondary button. */
  onWaiveClick?: () => void;
  /** Optional. When provided, renders an "Exempt" secondary button. */
  onExemptClick?: () => void;
}) => {
  const { formatMessage } = useIntl();
  const currency = useCurrencyFormat();
  const penceToPounds = useConvertFromPenceFactory();
  const poundsToPence = useConvertFromPoundsFactory();
  const currencyFormatter = useCallback(
    (amountInPence: number) => currency.format(penceToPounds(amountInPence)),
    [currency, penceToPounds],
  );
  const currencySymbol = useMemo(
    () =>
      currency.formatToParts(0).find((part) => part.type === "currency")
        ?.value ?? "",
    [currency],
  );
  const user = useUser();
  const { getCurrentTime } = useServerTime();
  const methodOptions = useMethodOptions();
  const patientName = useFetchPatientNameRxdb(doc.patientId);
  const [busy, setBusy] = useState(false);

  const calculatedInvoice = Invoice.fromRecord(
    invoiceRecordFromDocument(doc),
  ).toRecord();
  const patientPayer = calculatedInvoice.payers.find(
    (p) => p.type === PayerType.Patient,
  ) as (typeof calculatedInvoice.payers)[number];
  const patientDueInPence = patientPayer.balance.due;
  const patientDueInPounds = penceToPounds(patientDueInPence);

  const invoiceTotalInPence = calculatedInvoice.balance.total;
  const alreadyPaidInPence =
    calculatedInvoice.balance.paid +
    calculatedInvoice.balance.waived +
    calculatedInvoice.balance.exempt;

  const validationSchema = useMemo(
    () =>
      object({
        [SUM_PATH]: array()
          .of(
            object({
              method: mixed<FormMethod>()
                .oneOf([PaymentMethod.Cash, PaymentMethod.Other])
                .required(),
              amount: number().positive().required(),
            }),
          )
          .min(1)
          .required(),
      }),
    [],
  );

  const formMethods = useForm<FormValues>({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      transactions: [
        { method: PaymentMethod.Cash, amount: patientDueInPounds },
      ],
    },
  });

  const { control, handleSubmit } = formMethods;
  const { fields, append, remove } = useFieldArray<FormValues>({
    control,
    name: SUM_PATH,
  });
  // `watch(SUM_PATH)` reads the right value but does not re-render this
  // component on keystroke in the browser, so the balance strip only caught up
  // when something else forced a render (PNE-558). `useWatch` subscribes
  // properly. jsdom does not reproduce the stall — do not swap this back.
  const watchedRows = useWatch({ control, name: SUM_PATH });
  const { sumInPence, diffInPence, canSubmit, balanceStatus } = useMemo(
    () => derivePaymentSummary(watchedRows, patientDueInPence, poundsToPence),
    [watchedRows, patientDueInPence, poundsToPence],
  );

  const balanceLabel = useMemo(() => {
    if (balanceStatus === "valid")
      return formatMessage({
        id: "invoicing.takePaymentV2.balance.complete",
        defaultMessage: "Balance complete",
      });
    if (balanceStatus === "invalid")
      return formatMessage(
        {
          id: "invoicing.takePaymentV2.balance.over",
          defaultMessage: "{amount} over the amount owed",
        },
        { amount: currencyFormatter(diffInPence) },
      );
    return formatMessage(
      {
        id: "invoicing.takePaymentV2.balance.remaining",
        defaultMessage: "{amount} still to allocate",
      },
      { amount: currencyFormatter(-diffInPence) },
    );
  }, [balanceStatus, diffInPence, currencyFormatter, formatMessage]);

  const addSplitRow = useCallback(() => {
    const usedMethods = (watchedRows ?? []).map((row) => row?.method);
    const nextMethod =
      methodOptions.find((option) => !usedMethods.includes(option.value))
        ?.value ?? PaymentMethod.Cash;
    const unallocatedInPounds = penceToPounds(
      Math.max(0, patientDueInPence - sumInPence),
    );
    append({ method: nextMethod, amount: unallocatedInPounds || "" });
  }, [
    append,
    methodOptions,
    patientDueInPence,
    penceToPounds,
    sumInPence,
    watchedRows,
  ]);

  const onSubmit = async (values: FormValues) => {
    setBusy(true);
    try {
      const timestamp = getCurrentTime().toISOString();
      const transactions: InvoiceTransaction[] = values.transactions.map(
        (row) => ({
          id: randomUniqueId(),
          uuid: null,
          type: TransactionType.Payment,
          status: TransactionStatus.Success,
          timestamp,
          actionedByUserId: user.id,
          amount: {
            currency: calculatedInvoice.balance.currency,
            value: poundsToPence(Number(row.amount)),
          },
          paymentMethod: row.method,
          payerName: patientPayer.name,
          payerType: PayerType.Patient,
        }),
      );
      await updateWithInsurerChargeIds(doc, {
        actionedByUserId: user.id,
        timestamp,
        createTransactionId: randomUniqueId,
        update: (invoice, insurerCharges) =>
          invoice.takePayment(transactions, insurerCharges).toRecord(),
      });
      onClose();
    } catch (err) {
      if (isServerTimeUnknownError(err)) {
        toast.error(formatMessage(serverTimeUnknownMessage));
      } else {
        toast.error(
          err instanceof Error
            ? err.message
            : formatMessage({
                id: "invoicing.takePaymentV2.error.generic",
                defaultMessage: "Failed to record payment",
              }),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const subtitle = patientName
    ? `${patientName} · ${doc.invoiceNumber}`
    : doc.invoiceNumber;

  return (
    <DialogContainer
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // The DS dialog paper is padded, but the redesign's summary band and
      // divider rules run edge to edge, so each band owns its own padding.
      PaperProps={{ style: { padding: 0 } }}
      data-testid="invoicing.takePaymentV2.modal"
    >
      <HeaderBand>
        <DialogHeader title="">
          <HeaderTitles>
            <SectionTitle>
              <FormattedMessage
                id="invoicing.takePaymentV2.title"
                defaultMessage="Take payment"
              />
            </SectionTitle>
            <SectionSubtitle
              color="textSecondary"
              data-testid="invoicing.takePaymentV2.subtitle"
            >
              {subtitle}
            </SectionSubtitle>
          </HeaderTitles>
          <QuietIconButton
            onClick={onClose}
            aria-label={formatMessage({
              id: "invoicing.takePaymentV2.close",
              defaultMessage: "Close",
            })}
            data-testid="invoicing.takePaymentV2.close"
          >
            <CloseIcon fontSize="small" />
          </QuietIconButton>
        </DialogHeader>
      </HeaderBand>

      <FormProvider {...formMethods}>
        <form name="take-payment-v2-form" onSubmit={handleSubmit(onSubmit)}>
          <SummaryBand>
            <div>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage
                  id="invoicing.takePaymentV2.amountOwed"
                  defaultMessage="Amount owed by patient"
                />
              </Typography>
              <OwedAmount
                variant="h1"
                data-testid="invoicing.takePaymentV2.amountOwed"
              >
                {currencyFormatter(patientDueInPence)}
              </OwedAmount>
            </div>
            <SummaryStats>
              <div>
                <StatCaption variant="caption">
                  <FormattedMessage
                    id="invoicing.takePaymentV2.invoiceTotal"
                    defaultMessage="Invoice total"
                  />
                </StatCaption>
                <Typography
                  variant="body1"
                  data-testid="invoicing.takePaymentV2.invoiceTotal"
                >
                  {currencyFormatter(invoiceTotalInPence)}
                </Typography>
              </div>
              <div>
                <StatCaption variant="caption">
                  <FormattedMessage
                    id="invoicing.takePaymentV2.alreadyPaid"
                    defaultMessage="Already paid"
                  />
                </StatCaption>
                <Typography
                  variant="body1"
                  data-testid="invoicing.takePaymentV2.alreadyPaid"
                >
                  {currencyFormatter(alreadyPaidInPence)}
                </Typography>
              </div>
            </SummaryStats>
          </SummaryBand>

          <DialogBody>
            <MethodsBand>
              <MethodsLabel variant="body2">
                <strong>
                  <FormattedMessage
                    id="invoicing.takePaymentV2.methods"
                    defaultMessage="Payment methods"
                  />
                </strong>
              </MethodsLabel>

              {fields.map((field, i) => (
                <MethodRow
                  key={field.id}
                  data-testid={`invoicing.takePaymentV2.row.${i}`}
                >
                  <MethodCell>
                    <SelectField
                      name={`${SUM_PATH}.${i}.method`}
                      label={formatMessage({
                        id: "invoicing.takePaymentV2.method.label",
                        defaultMessage: "Method",
                      })}
                      options={methodOptions}
                      disabled={busy}
                      hideHelper
                      fullWidth
                    />
                  </MethodCell>
                  <AmountCell>
                    <TextField
                      name={`${SUM_PATH}.${i}.amount`}
                      type="number"
                      label={formatMessage({
                        id: "invoicing.takePaymentV2.amount.label",
                        defaultMessage: "Amount",
                      })}
                      InputProps={{
                        startAdornment: (
                          <CurrencyAdornment>
                            {currencySymbol}
                          </CurrencyAdornment>
                        ),
                      }}
                      disabled={busy}
                      hideHelper
                      fullWidth
                    />
                  </AmountCell>
                  <RemoveCell>
                    {fields.length > 1 && (
                      <QuietIconButton
                        onClick={() => remove(i)}
                        disabled={busy}
                        aria-label={formatMessage({
                          id: "invoicing.takePaymentV2.removeRow",
                          defaultMessage: "Remove this payment method",
                        })}
                        data-testid={`invoicing.takePaymentV2.row.${i}.remove`}
                      >
                        {/* BinIcon defaults to delete-red; removing an
                            unsubmitted split row is not destructive. */}
                        <BinIcon fill="currentColor" fontSize="small" />
                      </QuietIconButton>
                    )}
                  </RemoveCell>
                </MethodRow>
              ))}

              {fields.length < methodOptions.length && (
                <SplitAction>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={addSplitRow}
                    disabled={busy}
                    data-testid="invoicing.takePaymentV2.split"
                  >
                    <FormattedMessage
                      id="invoicing.takePaymentV2.split"
                      defaultMessage="+ Split across another method"
                    />
                  </Button>
                </SplitAction>
              )}
            </MethodsBand>

            <BalanceStrip>
              <BalanceReadout variant="body2">
                <FormattedMessage
                  id="invoicing.takePaymentV2.allocated"
                  defaultMessage="<label>Allocated</label> {allocated} <label>of</label> {owed}"
                  values={{
                    allocated: currencyFormatter(sumInPence),
                    owed: currencyFormatter(patientDueInPence),
                    label: (chunks: React.ReactNode) => <span>{chunks}</span>,
                  }}
                />
              </BalanceReadout>
              <BalanceChip
                status={balanceStatus}
                label={balanceLabel}
                size="small"
                data-testid="invoicing.takePaymentV2.balanceChip"
                data-status={balanceStatus}
              />
            </BalanceStrip>
          </DialogBody>

          <FooterBand>
            <DialogFooter>
              <SecondaryActions>
                {onWaiveClick && (
                  <Button
                    variant="text"
                    color="primary"
                    onClick={onWaiveClick}
                    disabled={busy}
                    data-testid="invoicing.takePaymentV2.waive"
                  >
                    <FormattedMessage
                      id="invoicing.takePaymentV2.waive"
                      defaultMessage="Waive"
                    />
                  </Button>
                )}
                {onExemptClick && (
                  <Button
                    variant="text"
                    color="primary"
                    onClick={onExemptClick}
                    disabled={busy}
                    data-testid="invoicing.takePaymentV2.exempt"
                  >
                    <FormattedMessage
                      id="invoicing.takePaymentV2.exempt"
                      defaultMessage="Exempt"
                    />
                  </Button>
                )}
              </SecondaryActions>
              <Button
                type="submit"
                color="primary"
                data-testid="invoicing.takePaymentV2.accept"
                disabled={busy || !canSubmit}
                loading={busy}
              >
                {canSubmit ? (
                  <FormattedMessage
                    id="invoicing.takePaymentV2.acceptAmount"
                    defaultMessage="Take payment {amount}"
                    values={{ amount: currencyFormatter(sumInPence) }}
                  />
                ) : (
                  <FormattedMessage
                    id="invoicing.takePaymentV2.accept"
                    defaultMessage="Take payment"
                  />
                )}
              </Button>
            </DialogFooter>
          </FooterBand>
        </form>
      </FormProvider>
    </DialogContainer>
  );
};
